// ---------------------------------------------------------------------------
// DataTable, ported from the design system (R9)
//
// <sdr-data-table>: the list view for campaigns, leads, accounts and replies —
// sortable headers, optional row selection, rows that open or expand, a
// loading and an empty state, a sticky header.
//
// A light-DOM Lit element, because the system's DataTable holds state: the
// selection, the sort and the open rows. Each works as the system's
// useControlled: a value given from outside (selected, sort, expanded) wins,
// and a change is only reported (onSelectChange, onSort, onExpandChange);
// without it the element keeps its own, starting with nothing selected, no
// sort and defaultExpanded. `sort: null` is a given value (no sort), as in the
// system. Sorting is built in (sortValue, else the cell; numbers by value,
// the rest by the reader's locale); with onSort the rows stay in the order
// they are given, to be sorted on the server.
//
//   - Rows that open (onRowClick) are in the tab order (tabindex 0,
//     is-clickable for the focus ring); Enter or Space on the row itself opens
//     it like a click and is prevented. A key from inside the row is left
//     alone.
//   - Rows that expand (expandable(row) returns the details, or null): a ghost
//     chevron button at the start of the row, aria-expanded, and aria-controls
//     naming the expansion row while it is open. The row stays the same
//     element when it opens or closes, so the chevron keeps the focus. The
//     system's row does not: its <tr key> becomes a <Fragment key> and React
//     mounts a new row, so the focus falls to the page (seen with react-dom
//     18.3.1 in happy-dom). The markup is the same; the fix is proposed for the
//     system's next version.
//   - The selection boxes are the checkbox() template from toggles.ts, the
//     head one indeterminate for a partial selection; select all takes the
//     rows in the order they are given. A click on a box or a chevron does not
//     open the row.
//   - Rows and cells are keyed as in the system (React's key: the row's key,
//     the column's key), so a row or a column that moves keeps its elements.
//     A moved node loses the focus, so, like React's restoreSelection, the
//     element that had it gets it back after the update: a row the sort
//     moves, a box or chevron in it, a sort button whose column moved.
//
// Renamed, so the element keeps its own class: className → tableClass (on the
// wrapper, as in the system); label, the table's accessible name, is
// tableLabel, as the other element ports name theirs (groupLabel, navLabel).
// Every other prop keeps the system's name.
//
// In a flush Panel: the element sits between the panel body and the
// .sdr-table-wrap. Since R6 (S8) the system's rule is a descendant selector,
// `.sdr-panel__body--flush .sdr-table-wrap` (no border, radius or
// background), so it reaches the table through the element, as the padding
// rules always did; components.css needs no second selector for it.
//
// Written locally, because helpers.ts has no equivalent: the system's
// useControlled (as in toggles.ts and choices.ts), useId (a counter, as in
// dialog.ts), React's restoreSelection (as in nav.ts) and React's style
// lengths (a number is px) for the bar's attribute text. Cells, headers and
// details go through node() and a row's Enter and Space through reactKey(),
// both from helpers.ts.
//
// The frame's max-height and each header's width are set as React sets a
// style prop (helpers.ts's reactStyle, R10f review): through the CSSOM, only
// what changed, a page's own declaration on the frame or a header kept. The
// bar's track, fill and target stay attribute text: happy-dom's CSSOM cannot
// hold the fill's max(), so table.dom.ts reads the bar against the system's
// server text (shared-rules.dom.ts counts these three).
//
// R10f: the column options of the system's v13 (R6g, CHANGES.md D1-D3), as
// its tableCell() and tableBar() write them. Without them a cell is what it
// was in R9.
//   - muted: is-muted on the column's cells (ink-muted, for Updated, Owner).
//   - secondary(row): span.sdr-table__stack > span.sdr-table__main (the
//     value) + span.sdr-table__secondary; nothing for null, undefined, false
//     or empty text, and, the Lit way of saying null, for nothing. 0 and true
//     give a line (true an empty one, as React draws it). In a compact table
//     the stylesheet sets it beside the value.
//   - bar: span.sdr-table__bar-cell with the figure (span.sdr-table__figure)
//     and the track (span.sdr-table__bar, aria-hidden) holding the fill and a
//     target tick; figure first in a right-aligned column, track first
//     otherwise. The two are keyed as the system keys them ('fig', 'bar'), so
//     a column that changes its alignment moves both, as React does. One
//     scale per column: bar.max (a number, or per row), else the largest
//     number among the rows (0 if none). A value fills only when it is a
//     number above 0 on a scale above 0, at least 3px; places are clamped to
//     the track and written with one decimal and no locale (61.9%), as the
//     system writes them. tone caution or critical wins over color (chart-1
//     to chart-4; any other name is none), adds the alert icon on the
//     figure's far side from the track and, without a label, the words
//     "approaching the cap" or "cap reached"; label(row) is read after the
//     figure (", 2,980 of 4,812 leads") in span.sdr-visually-hidden. No tab
//     stop, no readout: the figure is the bar's label (README).
// The row's ground for the target's ring (--sdr-table-ground) and the compact
// layout are the stylesheet's (components.css, D4).
//   - Callbacks are called as the system calls them, each with the row alone:
//     value(row) for the column scale before the sort's sortValue(row), then
//     row by row expandable, expandLabel and the cells (render, the bar's
//     value, max, target, tone, color, label, then secondary). So a row's
//     cells are drawn while the row is, not inside repeat, whose commit order
//     after a sort is not the rows' order.
//   - One difference nothing shows: when a second line comes or goes, React
//     keeps the cell's outer span (the bar cell becomes the stack, or back)
//     and remakes what is in it; the port makes a new span. Both remake the
//     figure and the bar, so a focus inside goes in both.
// ---------------------------------------------------------------------------

import { html, LitElement, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';

import { button } from './button.js';
import { currentLocale, cx, noBox, node, num, present, reactKey, reactStyle } from './helpers.js';
import { icon } from './icons.js';
import { checkbox } from './toggles.js';

export type RowKey = string | number;

/** A chart token, when a bar stands for a series (Email chart-1, LinkedIn chart-2, Phone chart-3; chart-4 the grey). */
export type BarColor = 'chart-1' | 'chart-2' | 'chart-3' | 'chart-4';

/** A small meter beside a column's figure (the system's ColumnBar). */
export interface ColumnBar<Row = Record<string, unknown>> {
  /** The bar's value. Default: the row's field (row[column.key]) when it is a number. */
  value?: (row: Row) => number;
  /** The full bar: a number for the whole column, or per row (the share of a whole). Default: the largest value among the rows. */
  max?: number | ((row: Row) => number);
  /** A tick on the track at this value: a target or a cap. Name it in the header or right beside the table. */
  target?: number | ((row: Row) => number);
  /** Only when the bar stands for a series. Default ink-muted. */
  color?: BarColor | ((row: Row) => BarColor | null | undefined);
  /** Only when the value needs a person (near or over a cap). Wins over color; adds the alert icon and words. */
  tone?: (row: Row) => 'caution' | 'critical' | null | undefined;
  /** What the bar shows that the figure does not, read after the figure (", 2,980 of 4,812 leads"). */
  label?: (row: Row) => string | null | undefined;
  /** Track width; a number is px. Default 56px. */
  width?: number | string;
}

export interface Column<Row = Record<string, unknown>> {
  key: string;
  header: unknown;
  /** 'right' for numbers. */
  align?: 'left' | 'right';
  /** A number is px. */
  width?: number | string;
  sortable?: boolean;
  /** Sort by something other than the cell. */
  sortValue?: (row: Row) => number | string;
  /** IDs. */
  mono?: boolean;
  /** The name column. */
  primary?: boolean;
  /** The cell's content (a badge); without it the value, numbers in the reader's locale. */
  render?: (row: Row) => unknown;
  /** A second line under the value, caption size in ink-muted; beside it in a compact table. null (or nothing) for none. */
  secondary?: (row: Row) => unknown;
  /** An inline bar beside the figure. */
  bar?: ColumnBar<Row>;
  /** A supporting column (Updated, Owner) in ink-muted. */
  muted?: boolean;
}

export interface SortState {
  key: string;
  dir: 'asc' | 'desc';
}

/** React's style length, for the bar's attribute text: a number is px. */
const length = (v: number | string) => (typeof v === 'number' ? `${v}px` : String(v).trim());

const stop = (event: Event) => event.stopPropagation();

/** The chart tokens a bar takes (the system's BAR_COLORS); any other name is none. */
const BAR_COLORS: Record<string, 1> = { 'chart-1': 1, 'chart-2': 1, 'chart-3': 1, 'chart-4': 1 };

/** The track's two parts in their order: figure first in a right-aligned column, track first otherwise. */
const FIGURE_FIRST = ['fig', 'bar'] as const;
const BAR_FIRST = ['bar', 'fig'] as const;

/** A bar's number: value(row), else the row's field. */
function barValue<Row>(c: Column<Row>, r: Row): unknown {
  return c.bar?.value ? c.bar.value(r) : (r as Record<string, unknown>)[c.key];
}

/** A place on the track as the system writes it: clamped to 0 to 1, one decimal, no locale (61.9%). */
function barPos(v: unknown, max: unknown): string {
  return `${(Math.max(0, Math.min(1, (v as number) / (max as number))) * 100).toFixed(1)}%`;
}

/** The largest number in a bar column without its own max: one scale for the column, 0 when none is above it. */
function barMaxOf<Row>(c: Column<Row>, rows: Row[]): number {
  return rows.reduce<number>((mx, r) => {
    const v = barValue(c, r);
    return typeof v === 'number' && v > mx ? v : mx;
  }, 0);
}

/** The figure with its meter (the system's tableBar). */
function tableBar<Row>(c: Column<Row>, r: Row, figure: unknown, colMax: number | undefined): TemplateResult {
  const b = c.bar as ColumnBar<Row>;
  const v = barValue(c, r);
  const max = typeof b.max === 'function' ? b.max(r) : b.max != null ? b.max : colMax;
  const target = typeof b.target === 'function' ? b.target(r) : b.target;
  const tone = b.tone ? b.tone(r) : null;
  const color = typeof b.color === 'function' ? b.color(r) : b.color;
  const label = b.label ? b.label(r) : null;
  const filled = typeof v === 'number' && v > 0 && (max as number) > 0;
  const toned = tone === 'caution' || tone === 'critical';
  const kind = toned ? tone : Object.hasOwn(BAR_COLORS, color as string) ? color : null;
  const bar = html`<span
    class=${cx('sdr-table__bar', kind && `sdr-table__bar--${kind}`)}
    style=${ifDefined(b.width ? `width:${length(b.width)}` : undefined)}
    aria-hidden="true"
  ><span class="sdr-table__bar-fill" style=${filled ? `width:max(3px, ${barPos(v, max)})` : 'width:0'}></span>${
    target != null && (max as number) > 0
      ? html`<span class="sdr-table__bar-target" style=${`left:${barPos(target, max)}`}></span>`
      : nothing
  }</span>`;
  // Status never rides on colour alone: the alert icon on the figure's side away from the track, and words.
  const right = c.align === 'right';
  const warn = toned
    ? icon({ name: 'alert', size: 12, className: `sdr-table__bar-icon sdr-table__bar-icon--${kind}` })
    : nothing;
  const said = label || (toned ? (kind === 'critical' ? 'cap reached' : 'approaching the cap') : null);
  const fig = html`<span class="sdr-table__figure">${right ? warn : nothing}${node(figure)}${right ? nothing : warn}</span>`;
  return html`<span class="sdr-table__bar-cell">${repeat(
    right ? FIGURE_FIRST : BAR_FIRST,
    part => part,
    part => (part === 'fig' ? fig : bar),
  )}${said ? html`<span class="sdr-visually-hidden">${`, ${said}`}</span>` : nothing}</span>`;
}

/** A column's cell: its value (render, or the field, numbers in the locale), its bar, its second line (tableCell). */
function tableCell<Row>(c: Column<Row>, r: Row, colMax: number | undefined): unknown {
  const record = r as Record<string, unknown>;
  let main: unknown = c.render ? c.render(r) : typeof record[c.key] === 'number' ? num(record[c.key]) : record[c.key];
  if (c.bar) main = tableBar(c, r, main, colMax);
  const sub = c.secondary ? c.secondary(r) : null;
  if (sub == null || sub === false || sub === '' || sub === nothing) return node(main);
  return html`<span class="sdr-table__stack"><span class="sdr-table__main">${node(main)}</span><span
    class="sdr-table__secondary"
  >${node(sub)}</span></span>`;
}

let instances = 0;

export class SdrDataTable<Row extends object = Record<string, unknown>> extends LitElement {
  static override properties = {
    columns: { attribute: false },
    rows: { attribute: false },
    rowKey: {},
    tableLabel: {},
    density: {},
    selectable: { type: Boolean },
    selected: { attribute: false },
    onSelectChange: { attribute: false },
    sort: { attribute: false },
    onSort: { attribute: false },
    onRowClick: { attribute: false },
    expandable: { attribute: false },
    expandLabel: { attribute: false },
    expanded: { attribute: false },
    defaultExpanded: { attribute: false },
    onExpandChange: { attribute: false },
    loading: { type: Boolean },
    loadingText: { attribute: false },
    empty: { attribute: false },
    maxHeight: { attribute: false },
    tableClass: { attribute: false },
    ownSelected: { state: true },
    ownSort: { state: true },
    ownExpanded: { state: true },
  };

  declare columns: Column<Row>[];
  /** Each with a unique `id`, or the field rowKey names. */
  declare rows: Row[];
  declare rowKey: string | undefined;
  /** The table's accessible name. */
  declare tableLabel: string | undefined;
  declare density: 'default' | 'compact';
  declare selectable: boolean;
  declare selected: RowKey[] | undefined;
  declare onSelectChange: ((keys: RowKey[]) => void) | undefined;
  declare sort: SortState | null | undefined;
  /** Sort on the server: the rows stay in the order they are given. */
  declare onSort: ((sort: SortState) => void) | undefined;
  /** Clickable rows are focusable; Enter and Space open them. */
  declare onRowClick: ((row: Row) => void) | undefined;
  /** A row's expansion, or null for a row that has none. */
  declare expandable: ((row: Row) => unknown) | undefined;
  declare expandLabel: ((row: Row) => string) | undefined;
  declare expanded: RowKey[] | undefined;
  declare defaultExpanded: RowKey[] | undefined;
  declare onExpandChange: ((keys: RowKey[]) => void) | undefined;
  /** Dims the rows the table has, or shows a spinner and loadingText instead of the empty message. */
  declare loading: boolean;
  declare loadingText: unknown;
  /** What to do next, for a table without rows. */
  declare empty: unknown;
  declare maxHeight: number | string | undefined;
  declare tableClass: string | undefined;
  protected declare ownSelected: RowKey[] | undefined;
  protected declare ownSort: SortState | null | undefined;
  protected declare ownExpanded: RowKey[] | undefined;

  private readonly base = `sdr-table-${++instances}`;
  private focusedBefore: HTMLElement | null = null;

  constructor() {
    super();
    this.columns = [];
    this.rows = [];
    this.density = 'default';
    this.selectable = false;
    this.loading = false;
  }

  override createRenderRoot() {
    return this;
  }

  override connectedCallback() {
    super.connectedCallback();
    noBox(this);
  }

  protected override willUpdate(_changed: PropertyValues<this>) {
    // React's useState(initial): the defaults are read once, at the first render.
    if (!this.hasUpdated) {
      this.ownSelected = [];
      this.ownSort = null;
      this.ownExpanded = this.defaultExpanded || [];
    }
    const at = document.activeElement;
    this.focusedBefore = at instanceof HTMLElement && this.contains(at) ? at : null;
  }

  protected override updated(_changed: PropertyValues<this>) {
    // React's restoreSelection: the element that had focus gets it back if it is still in the page.
    const was = this.focusedBefore;
    this.focusedBefore = null;
    if (was?.isConnected && document.activeElement !== was) was.focus({ preventScroll: true });
  }

  private keyOf(row: Row): RowKey {
    return (row as Record<string, unknown>)[this.rowKey || 'id'] as RowKey;
  }

  private get currentSelected(): RowKey[] {
    return this.selected !== undefined ? this.selected : this.ownSelected || [];
  }

  private get currentSort(): SortState | null {
    return this.sort !== undefined ? this.sort : this.ownSort || null;
  }

  private get currentExpanded(): RowKey[] {
    return this.expanded !== undefined ? this.expanded : this.ownExpanded || [];
  }

  private setSelected(next: RowKey[]) {
    if (this.selected === undefined) this.ownSelected = next;
    else this.requestUpdate(); // controlled: draw the given selection again, whatever the box did
    this.onSelectChange?.(next);
  }

  private setSort(next: SortState) {
    if (this.sort === undefined) this.ownSort = next;
    this.onSort?.(next);
  }

  private setExpanded(next: RowKey[]) {
    if (this.expanded === undefined) this.ownExpanded = next;
    this.onExpandChange?.(next);
  }

  private toggleSelected(k: RowKey) {
    const cur = this.currentSelected;
    this.setSelected(cur.indexOf(k) >= 0 ? cur.filter(x => x !== k) : cur.concat([k]));
  }

  private toggleOpen(k: RowKey) {
    const cur = this.currentExpanded;
    this.setExpanded(cur.indexOf(k) >= 0 ? cur.filter(x => x !== k) : cur.concat([k]));
  }

  /** The same column again turns the direction; a new one starts ascending, a number column descending. */
  private sortBy(c: Column<Row>) {
    const s0 = this.currentSort;
    this.setSort(
      s0 && s0.key === c.key
        ? { key: c.key, dir: s0.dir === 'asc' ? 'desc' : 'asc' }
        : { key: c.key, dir: c.align === 'right' ? 'desc' : 'asc' },
    );
  }

  private sorted(rows: Row[], cols: Column<Row>[]): Row[] {
    const s = this.currentSort;
    if (!s || this.onSort) return rows;
    const col = cols.filter(c => c.key === s.key)[0];
    const val = (r: Row) => (col?.sortValue ? col.sortValue(r) : (r as Record<string, unknown>)[s.key]);
    return rows.slice().sort((a, b) => {
      const x = val(a);
      const y = val(b);
      const c =
        typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), currentLocale());
      return s.dir === 'desc' ? -c : c;
    });
  }

  /**
   * A row's cells, keyed by column. Their contents are drawn here, while the row is, in column order: the system
   * calls render, the bar's callbacks and secondary row by row, right after the row's expandable and expandLabel.
   * Inside repeat they would wait for the commit and follow its order instead of the rows'.
   */
  private cells(r: Row, cols: Column<Row>[], barMax: Record<string, number>) {
    const contents = cols.map(c => tableCell(c, r, barMax[c.key]));
    return repeat(
      cols,
      c => c.key,
      (c, j) =>
        html`<td
          class=${cx(c.align === 'right' && 'is-num', c.mono && 'is-mono', c.primary && 'is-primary', c.muted && 'is-muted')}
        >${contents[j]}</td>`,
    );
  }

  private row(r: Row, i: number, cols: Column<Row>[], span: number, barMax: Record<string, number>): TemplateResult {
    const k = this.keyOf(r);
    const isSel = this.currentSelected.indexOf(k) >= 0;
    const detail = this.expandable ? this.expandable(r) : null;
    const isOpen = detail != null && this.currentExpanded.indexOf(k) >= 0;
    const xid = `${this.base}-x${i}`;
    const open = this.onRowClick;
    const tr = html`<tr
      class=${cx(isSel && 'is-selected', open && 'is-clickable', isOpen && 'is-expanded')}
      aria-selected=${ifDefined(this.selectable ? String(isSel) : undefined)}
      tabindex=${ifDefined(open ? 0 : undefined)}
      @click=${open ? () => open(r) : nothing}
      @keydown=${
        open
          ? (e: KeyboardEvent) => {
              if (e.target === e.currentTarget && (reactKey(e) === 'Enter' || reactKey(e) === ' ')) {
                e.preventDefault();
                open(r);
              }
            }
          : nothing
      }
    >${
      this.selectable
        ? html`<td class="sdr-table__check" @click=${stop}>${checkbox({
            ariaLabel: 'Select row',
            checked: isSel,
            onChange: () => this.toggleSelected(k),
          })}</td>`
        : nothing
    }${
      this.expandable
        ? html`<td class="sdr-table__toggle" @click=${stop}>${
            detail != null
              ? button({
                  variant: 'ghost',
                  size: 'sm',
                  icon: isOpen ? 'chevron-down' : 'chevron-right',
                  ariaExpanded: isOpen,
                  ariaControls: isOpen ? xid : undefined,
                  ariaLabel: this.expandLabel ? this.expandLabel(r) : 'Show details',
                  onClick: () => this.toggleOpen(k),
                })
              : nothing
          }</td>`
        : nothing
    }${this.cells(r, cols, barMax)}</tr>`;
    // One template, open or closed: the row stays the same element, so its chevron keeps the focus (see above).
    return html`${tr}${
      isOpen ? html`<tr class="sdr-table__expansion" id=${xid}><td colspan=${span}>${node(detail)}</td></tr>` : nothing
    }`;
  }

  override render() {
    const rows = this.rows || [];
    const cols = this.columns || [];
    const selected = this.currentSelected;
    const s = this.currentSort;
    // A bar column compares its rows on one scale: the largest value among the rows, unless bar.max says otherwise.
    // Taken before the sort, as the system takes it: value(row) is called for the scale before sortValue(row).
    const barMax: Record<string, number> = {};
    for (const c of cols) if (c.bar && c.bar.max == null) barMax[c.key] = barMaxOf(c, rows);
    const sorted = this.sorted(rows, cols);
    const all = rows.length > 0 && selected.length === rows.length;
    const some = selected.length > 0 && !all;
    const span = cols.length + (this.selectable ? 1 : 0) + (this.expandable ? 1 : 0);
    const body =
      sorted.length === 0
        ? html`<tr><td colspan=${span} class="sdr-table__empty">${
            this.loading
              ? html`<span class="sdr-table__loading"><span class="sdr-spinner" aria-hidden="true"></span>${node(
                  present(this.loadingText) ? this.loadingText : 'Loading',
                )}</span>`
              : node(present(this.empty) ? this.empty : 'Nothing here yet.')
          }</td></tr>`
        : repeat(
            sorted,
            r => this.keyOf(r),
            (r, i) => this.row(r, i, cols, span, barMax),
          );
    return html`<div
      class=${cx('sdr-table-wrap', this.tableClass)}
      style=${reactStyle(this.maxHeight ? { maxHeight: this.maxHeight } : undefined)}
    ><table
      class=${cx('sdr-table', this.density === 'compact' && 'sdr-table--compact', this.selectable && 'sdr-table--selectable')}
      aria-label=${ifDefined(this.tableLabel)}
      aria-busy=${ifDefined(this.loading ? 'true' : undefined)}
    ><thead><tr>${
      this.selectable
        ? html`<th class="sdr-table__check" scope="col">${checkbox({
            ariaLabel: 'Select all rows',
            checked: all,
            indeterminate: some,
            onChange: () => this.setSelected(all ? [] : rows.map(r => this.keyOf(r))),
          })}</th>`
        : nothing
    }${
      this.expandable
        ? html`<th class="sdr-table__toggle" scope="col"><span class="sdr-visually-hidden">Details</span></th>`
        : nothing
    }${repeat(
      cols,
      c => c.key,
      c => {
        const active = !!s && s.key === c.key;
        return html`<th
        scope="col"
        style=${reactStyle(c.width ? { width: c.width } : undefined)}
        class=${cx(c.align === 'right' && 'is-num')}
        aria-sort=${ifDefined(active && s ? (s.dir === 'asc' ? 'ascending' : 'descending') : undefined)}
      >${
        c.sortable
          ? html`<button type="button" class=${cx('sdr-table__sort', active && 'is-active')} @click=${() =>
              this.sortBy(c)}>${node(c.header)}${icon({
              name: active && s?.dir === 'asc' ? 'arrow-up' : 'arrow-down',
              size: 12,
              className: 'sdr-table__sort-icon',
            })}</button>`
          : node(c.header)
      }</th>`;
      },
    )}</tr></thead><tbody class=${ifDefined(this.loading && sorted.length ? 'is-loading' : undefined)}>${body}</tbody></table></div>`;
  }
}

if (!customElements.get('sdr-data-table')) customElements.define('sdr-data-table', SdrDataTable);

declare global {
  interface HTMLElementTagNameMap {
    'sdr-data-table': SdrDataTable;
  }
}
